import {
  CopyButton,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { t } from "@/lib/i18n/interpolate";
import {
  type AgeResult,
  CAMBRIDGE_HALF_LIFE,
  CAMBRIDGE_MEAN_LIFE,
  type ForwardResult,
  type Fraction,
  LIBBY_HALF_LIFE,
  LIBBY_MEAN_LIFE,
  referenceRows,
} from "@/lib/tool-kit/chem-reference/radiocarbon";
import {
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";

export interface RadiocarbonResultStrings {
  libbyAge: string;
  cambridgeAge: string;
  fractionModern: string;
  percentModern: string;
  yearsBp: string;
  ageWithSigma: string;
  modernNote: string;
  limitNote: string;
  calibrationNote: string;
  working: string;
  workFractionation: string;
  workFraction: string;
  workLibby: string;
  workCambridge: string;
  workReverse: string;
  workSigma: string;
  tableHeading: string;
  columnPmc: string;
  columnLibby: string;
  columnCambridge: string;
  copy: string;
  copied: string;
  copyAge: string;
  copyPmc: string;
}

const SIG = 4;

/** Whole years with grouping: 9,672. */
function years(fmt: NumberFormatter, value: number): string {
  return fmt(Math.round(value), { maximumFractionDigits: 0 });
}

function ageText(
  fmt: NumberFormatter,
  strings: RadiocarbonResultStrings,
  age: number,
  sigma: number
): string {
  return sigma > 0
    ? t(strings.ageWithSigma, {
        age: years(fmt, age),
        sigma: years(fmt, sigma),
      })
    : years(fmt, age);
}

function ReferenceTable({
  fmt,
  strings,
}: {
  fmt: NumberFormatter;
  strings: RadiocarbonResultStrings;
}) {
  const rows = referenceRows().map((row) => [
    formatSig(fmt, row.pmc, SIG),
    years(fmt, row.libby),
    years(fmt, row.cambridge),
  ]);
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[strings.columnPmc, strings.columnLibby, strings.columnCambridge]}
        rows={rows}
      />
    </div>
  );
}

export function AgeResults({
  result,
  fraction,
  fmt,
  strings,
}: {
  result: AgeResult;
  fraction: Fraction;
  fmt: NumberFormatter;
  strings: RadiocarbonResultStrings;
}) {
  const f = formatSig(fmt, result.f14c, SIG);
  const pmc = formatSig(fmt, result.pmc, SIG);
  const lines: string[] = [];
  if (fraction.fractionationFactor !== 1) {
    lines.push(
      t(strings.workFractionation, {
        factor: formatSig(fmt, fraction.fractionationFactor, SIG),
      })
    );
  }
  lines.push(t(strings.workFraction, { f, pmc }));
  if (!result.modern) {
    lines.push(
      t(strings.workLibby, {
        life: fmt(LIBBY_MEAN_LIFE),
        f,
        age: years(fmt, result.libbyAge),
      }),
      t(strings.workCambridge, {
        half: fmt(CAMBRIDGE_HALF_LIFE),
        life: formatSig(fmt, CAMBRIDGE_MEAN_LIFE, SIG),
        f,
        age: years(fmt, result.cambridgeAge),
      })
    );
  }
  if (!result.modern && result.libbySigma > 0) {
    lines.push(
      t(strings.workSigma, {
        sigma: years(fmt, result.libbySigma),
        life: fmt(LIBBY_MEAN_LIFE),
        relative: formatSig(fmt, (fraction.sigma / fraction.f14c) * 100, 3),
      })
    );
  }
  const copy = result.modern
    ? t(strings.copyPmc, { pmc, f })
    : t(strings.copyAge, {
        age: ageText(fmt, strings, result.libbyAge, result.libbySigma),
        pmc,
      });
  return (
    <>
      {result.modern ? (
        <Notice>{strings.modernNote}</Notice>
      ) : (
        <StatGrid>
          <StatCard
            emphasis
            label={t(strings.libbyAge, { half: fmt(LIBBY_HALF_LIFE) })}
            value={`${ageText(fmt, strings, result.libbyAge, result.libbySigma)} ${strings.yearsBp}`}
          />
          <StatCard
            label={t(strings.cambridgeAge, { half: fmt(CAMBRIDGE_HALF_LIFE) })}
            value={`${ageText(fmt, strings, result.cambridgeAge, result.cambridgeSigma)} ${strings.yearsBp}`}
          />
          <StatCard label={strings.fractionModern} value={f} />
          <StatCard label={strings.percentModern} value={pmc} />
        </StatGrid>
      )}
      {result.beyondLimit ? <Notice>{strings.limitNote}</Notice> : null}
      <Notice>{strings.calibrationNote}</Notice>
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        {lines.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
      <ReferenceTable fmt={fmt} strings={strings} />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={copy}
      />
    </>
  );
}

export function PercentResults({
  result,
  age,
  sigma,
  fmt,
  strings,
}: {
  result: ForwardResult;
  age: number;
  sigma: number;
  fmt: NumberFormatter;
  strings: RadiocarbonResultStrings;
}) {
  const f = formatSig(fmt, result.f14c, SIG);
  const pmc = formatSig(fmt, result.pmc, SIG);
  const pmcText =
    sigma > 0 ? `${pmc} ± ${formatSig(fmt, result.pmcSigma, 2)}` : pmc;
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.percentModern} value={pmcText} />
        <StatCard label={strings.fractionModern} value={f} />
      </StatGrid>
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>
          {t(strings.workReverse, {
            life: fmt(LIBBY_MEAN_LIFE),
            age: years(fmt, age),
            f,
          })}
        </p>
      </div>
      <Notice>{strings.calibrationNote}</Notice>
      <ReferenceTable fmt={fmt} strings={strings} />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyPmc, { pmc: pmcText, f })}
      />
    </>
  );
}
